// src/pages/admin/CountySetting.js
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useMainCompany } from "../../context/MainCompanyContext";
import AdminPageHeader from "../../components/common/AdminPageHeader";
import api from "../../api/axios";
import { Upload, Download, Loader2, Save } from "lucide-react";
import { formatCurrency, sanitizeNumberInput } from "../../utils/format";

export default function CountySetting() {
  const { selectedMain } = useMainCompany();
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [stateid, setstateid] = useState(0);
  const [countyid, setcountyid] = useState(0);

  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [loadingSetting, setLoadingSetting] = useState(false);
  const [saving, setSaving] = useState(false);

  const [setting, setSetting] = useState(null);
  const [form, setForm] = useState({ maxloanamount: "", vamaxloanamount: "" });
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);

  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const fileInputRef = useRef();

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  // ----------------- API LOADERS -----------------

  // Load states
  useEffect(() => {
    let mounted = true;
    setLoadingStates(true);
    setstateid(0);
    setcountyid(0);
    const params = {};
    if (selectedMain) params.maincompanyid = selectedMain;
    api
      .get("/admin/states", { params })
      .then((resp) => {
        if (!mounted) return;
        const payload = resp?.data ?? resp;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : [];
        setStates(list);
      })
      .finally(() => setLoadingStates(false));
    return () => {
      mounted = false;
    };
  }, [selectedMain]);

  // Load counties
  useEffect(() => {
    setCounties([]);
    setcountyid(0);
    if (!stateid) return;
    let mounted = true;
    setLoadingCounties(true);
    const params = { stateid };
    if (selectedMain) params.maincompanyid = selectedMain;
    api
      .get("/auth/counties", { params })
      .then((resp) => {
        if (!mounted) return;
        const payload = resp?.data ?? resp;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : [];
        setCounties(list);
      })
      .finally(() => setLoadingCounties(false));
    return () => {
      mounted = false;
    };
  }, [stateid, selectedMain]);

  // Load single county setting
  async function loadSetting(stateid, countyid, selectedMain) {
    setSetting(null);
    setForm({ maxloanamount: "", vamaxloanamount: "" });
    setErrors({});
    if (!stateid || !countyid) return;
    setLoadingSetting(true);
    try {
      const params = { stateid, countyid };
      if (selectedMain) params.maincompanyid = selectedMain;
      const resp = await api.get("/admin/county-setting", { params });
      const body = resp.data ?? resp;
      const rows = body && Array.isArray(body.data) ? body.data : body;
      if (rows && rows.length > 0) {
        const s = rows[0];
        setSetting(s);
        setForm({
          maxloanamount: formatCurrency(s.maxloanamount),
          vamaxloanamount: formatCurrency(s.vamaxloanamount),
        });
      }
    } finally {
      setLoadingSetting(false);
    }
  }

  // Load county list
  async function loadList(stateid, selectedMain) {
    if (!stateid) {
      setList([]);
      return;
    }
    setLoadingList(true);
    try {
      const params = { stateid };
      if (selectedMain) params.maincompanyid = selectedMain;
      const resp = await api.get("/admin/county-setting/list", { params });
      const body = resp?.data ?? resp;
      const rows = body && Array.isArray(body.data) ? body.data : body;
      setList(Array.isArray(rows) ? rows : []);
    } finally {
      setLoadingList(false);
    }
  }

  // Trigger both loaders when state/county changes
  useEffect(() => {
    loadSetting(stateid, countyid, selectedMain);
    loadList(stateid, selectedMain);
  }, [stateid, countyid, selectedMain]);

  // ----------------- VALIDATION -----------------

  const canSubmit = useMemo(() => {
    if (!stateid || !countyid) return false;
    const m1 =
      form.maxloanamount !== "" &&
      !isNaN(Number(String(form.maxloanamount).replace(/,/g, "")));
    const m2 =
      form.vamaxloanamount !== "" &&
      !isNaN(Number(String(form.vamaxloanamount).replace(/,/g, "")));
    return m1 && m2 && !saving;
  }, [stateid, countyid, form, saving]);

  function validate() {
    const e = {};
    const a = String(form.maxloanamount).trim();
    const b = String(form.vamaxloanamount).trim();
    if (a === "" || isNaN(Number(a.replace(/,/g, ""))))
      e.maxloanamount = "Enter a valid number";
    if (b === "" || isNaN(Number(b.replace(/,/g, ""))))
      e.vamaxloanamount = "Enter a valid number";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  // ----------------- ACTIONS -----------------

  async function onUpdate() {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        maxloanamount: Number(String(form.maxloanamount).replace(/,/g, "")),
        vamaxloanamount: Number(String(form.vamaxloanamount).replace(/,/g, "")),
      };
      if (selectedMain) payload.maincompanyid = selectedMain;
      const id = setting?.id ?? 0;
      const bodyPayload = { stateid, countyid, ...payload };
      if (selectedMain) bodyPayload.maincompanyid = selectedMain;
      await api.put(`/admin/county-setting/${id}`, bodyPayload);

      // ✅ refresh both form + list
      await loadSetting(stateid, countyid, selectedMain);
      await loadList(stateid, selectedMain);
    } finally {
      setSaving(false);
    }
  }

  async function onImportFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    if (selectedMain) formData.append("maincompanyid", selectedMain);
    try {
      await api.post("/admin/county-setting/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // ✅ refresh both form + list
      await loadSetting(stateid, countyid, selectedMain);
      await loadList(stateid, selectedMain);
    } finally {
      e.target.value = "";
    }
  }

  function onDownloadSample() {
    const blob = new Blob(
      [
        "state_name,county_name,maxloanamount,vamaxloanamount\n" +
          "California,Los Angeles,1000000,8000000\n",
      ],
      { type: "text/csv" }
    );
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "county-settings-sample.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  }

  // ----------------- UI -----------------

  return (
    <div className="space-y-6">
      <AdminPageHeader title="County Setting" />

      {/* Selectors + Import/Download */}
      <div className="bg-white rounded shadow p-6">
        <h3 className="font-semibold mb-3">Select State & County</h3>
        <div className="flex items-center justify-between gap-6">
          <div className="flex gap-4">
            <select
              value={stateid}
              onChange={(e) => setstateid(Number(e.target.value) || 0)}
              className="border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400"
            >
              <option value={0}>Select State</option>
              {loadingStates && <option>Loading...</option>}
              {states.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.statename}
                </option>
              ))}
            </select>

            <select
              value={countyid}
              onChange={(e) => setcountyid(Number(e.target.value) || 0)}
              disabled={!stateid}
              className="border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400"
            >
              <option value={0}>Select County</option>
              {loadingCounties && <option>Loading...</option>}
              {counties.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.countyname}
                </option>
              ))}
            </select>
          </div>

          {/* Import / Download */}
          <div className="flex gap-3">
            <button
              onClick={() => fileInputRef.current.click()}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
            >
              <Upload className="w-4 h-4" /> Import
            </button>
            <input
              type="file"
              accept=".xlsx,.csv"
              ref={fileInputRef}
              className="hidden"
              onChange={onImportFile}
            />
            <button
              onClick={onDownloadSample}
              className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition"
            >
              <Download className="w-4 h-4" /> Download Sample
            </button>
          </div>
        </div>
      </div>

      {/* Edit single county */}
      {stateid > 0 && (
        <div className="bg-white rounded shadow p-6">
          <h3 className="font-semibold mb-3">FHA / VA Setting</h3>
          {loadingSetting ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block mb-1 text-sm">FHA Max Loan Amount</label>
                <input
                  value={form.maxloanamount}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, maxloanamount: sanitizeNumberInput(e.target.value) }))
                  }
                  onBlur={(e) =>
                    setForm((f) => ({
                      ...f,
                      maxloanamount: formatCurrency(e.target.value),
                    }))
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400"
                />
                {errors.maxloanamount && (
                  <div className="text-red-600 text-xs mt-1">
                    {errors.maxloanamount}
                  </div>
                )}
              </div>
              <div>
                <label className="block mb-1 text-sm">VA Max Loan Amount</label>
                <input
                  value={form.vamaxloanamount}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, vamaxloanamount: sanitizeNumberInput(e.target.value) }))
                  }
                  onBlur={(e) =>
                    setForm((f) => ({
                      ...f,
                      vamaxloanamount: formatCurrency(e.target.value),
                    }))
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400"
                />
                {errors.vamaxloanamount && (
                  <div className="text-red-600 text-xs mt-1">
                    {errors.vamaxloanamount}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="flex justify-end mt-6">
            <button
              disabled={!canSubmit}
              onClick={onUpdate}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60 transition"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Updating...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" /> Save
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Table list */}
      {stateid > 0 && (
        <div className="bg-white rounded shadow p-6">
          <h3 className="font-semibold mb-3">All Counties in State</h3>
          {loadingList ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : list.length === 0 ? (
            <div className="text-sm text-gray-500">No records found</div>
          ) : (
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-gray-100 text-xs uppercase text-gray-600">
                  <th className="text-left px-3 py-2 border">County Name</th>
                  <th className="text-right px-3 py-2 border">FHA Max</th>
                  <th className="text-right px-3 py-2 border">VA Max</th>
                  <th className="text-right px-3 py-2 border" style={{ width: "320px" }}></th>
                </tr>
              </thead>
              <tbody>
                {list.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 text-sm border-t">
                    <td className="px-3 py-2 border">{row.countyname}</td>
                    <td className="px-3 py-2 border text-right">
                      {formatCurrency(row.maxloanamount, { currency: true })}
                    </td>
                    <td className="px-3 py-2 border text-right">
                      {formatCurrency(row.vamaxloanamount, { currency: true })}
                    </td>
                    <td className="px-3 py-2 border" style={{ width: "320px" }}></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
